import React from "react";
import "./App.scss";
import { FileExtended } from "./classes/fileExtended";
import ImagePreview from "./components/imagePreview";
import { MapInteractionCSS } from "react-map-interaction";
import classNames from "classnames";
import { MosaicService } from "./services/mosaicService";
import { Spinner } from "./components/spinner/spinner";
import { Slider } from "./components/slider/slider";
import GA4React from "ga-4-react";

interface IAppState {
  targetImage: FileExtended;
  sampleImages: FileExtended[];
  sampleSize: number;
  adjustColorVal: number;
  randomizeImages: boolean;
  showMenu: boolean;
  viewportValue: any;
  imageURL: string;
  rendering: boolean;
  targetImageChanged: boolean;
  renderedImage: boolean;
}

class App extends React.Component<any, IAppState> {
  constructor(props: any) {
    super(props);

    this.state = {
      targetImage: null as unknown as FileExtended,
      sampleImages: [],
      sampleSize: 10,
      adjustColorVal: 0.6,
      randomizeImages: false,
      showMenu: true,
      viewportValue: {
        scale: 1,
        translation: { x: window.innerWidth / 2 - 250, y: 20 },
      },
      imageURL: "sample.webp",
      rendering: false,
      targetImageChanged: false,
      renderedImage: false,
    };

    this.setTargetImage = this.setTargetImage.bind(this);
    this.setSampleImages = this.setSampleImages.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.renderMosaic = this.renderMosaic.bind(this);
    this.removeMainImage = this.removeMainImage.bind(this);
    this.removeSampleImage = this.removeSampleImage.bind(this);
    this.toggleMenu = this.toggleMenu.bind(this);
    this.downloadImage = this.downloadImage.bind(this);

    const ga4react = new GA4React("G-0SXTVL4X63");
    ga4react.initialize().then((ga4) => {
      ga4.pageview("/");
    });
  }

  setTargetImage(images: FileList | null) {
    if (images && images.length > 0 && !images[0].type.includes("webp")) {
      this.setState({ targetImage: new FileExtended(images[0]), targetImageChanged: true });
    }
  }

  setSampleImages(images: FileList | null) {
    if (images && images.length > 0) {
      let sampleImages = this.state.sampleImages;

      for (let i = 0; i < images.length; i++) {
        if (!images[i].type.includes("webp")) {
          let file = images.item(i);
          sampleImages.push(new FileExtended(file as File));
        }
      }
      this.setState({ sampleImages: sampleImages });
    }
  }

  removeSampleImage(index: number) {
    let sampleImages = this.state.sampleImages;
    sampleImages.splice(index, 1);
    this.setState({ sampleImages: sampleImages });
  }

  removeMainImage() {
    this.setState({ targetImage: null as unknown as FileExtended });
  }

  handleChange(name: string, value: any) {
    this.setState({ ...this.state, [name]: value });
  }

  toggleMenu() {
    this.setState({ showMenu: !this.state.showMenu });
  }

  async renderMosaic() {
    if (this.state.targetImage && this.state.sampleImages.length > 0 && !this.state.rendering) {
      this.setState({ rendering: true, showMenu: window.innerWidth > 768 });

      const mosaicURL = await new MosaicService().createMosaic(
        this.state.targetImage,
        this.state.sampleImages,
        this.state.sampleSize,
        this.state.adjustColorVal,
        this.state.randomizeImages
      );

      const viewScale = {
        scale: 1,
        translation: { x: window.innerWidth / 2 - 250, y: 20 },
      };
      this.setState({
        imageURL: mosaicURL,
        rendering: false,
        renderedImage: true,
        targetImageChanged: false,
        viewportValue: this.state.targetImageChanged ? viewScale : this.state.viewportValue,
      });
    }
  }

  downloadImage() {
    const link = document.createElement("a");
    link.download = "MosaicBuilderImage";
    link.href = this.state.imageURL;
    link.click();
  }

  render() {
    return (
      <div className="text-white h-full w-screen">
        <div className="w-full nav bg-gray-900 flex p-3">
          <button className="h-4 w-18 block" onClick={this.toggleMenu} aria-label="Expand/ Collapse sidebar">
            <svg viewBox="0 0 100 80" width="40" height="40" fill="#FFFFFF">
              <rect width="100" height="10"></rect>
              <rect y="30" width="100" height="10"></rect>
              <rect y="60" width="100" height="10"></rect>
            </svg>
          </button>

          <span className="block pl-3 text-2xl">Mosaic Builder</span>
        </div>

        <div className="relative">
          <div className="bg-black height-container overflow-hidden flex justify-center items-center">
            {this.state.rendering ? (
              <div className={classNames("text-center", { "ml-48": this.state.showMenu })}>
                <div className="w-12 h-12 block m-auto">
                  <Spinner></Spinner>
                </div>
                <div className="mt-2">This is rendering using your browser.<br/>It may take a while or become unresponsive...</div>
              </div>
            ) : (
              <MapInteractionCSS value={this.state.viewportValue} onChange={(value: any) => this.setState({ ...this.state, viewportValue: value })}>
                <img src={this.state.imageURL} alt="Preview of mosaic" />
              </MapInteractionCSS>
            )}
          </div>

          <div className={classNames("height-container", "absolute", "top-0", "w-96", "select-none", "bg-gray-800", "sidemenu", { "show-menu": this.state.showMenu })}>
            <div className="p-8 sidebar-height overflow-auto">
              <div>
                <h2 className="text-2xl">Settings</h2>

                <div className="w-full form-group mt-4">
                  <label className="form-label" htmlFor="sampleSize">
                    Sample Size
                  </label>
                  <Slider onChange={this.handleChange} min={1} max={100} step={1} name="sampleSize" value={this.state.sampleSize}></Slider>
                </div>

                <div className="w-full form-group">
                  <label className="form-label" htmlFor="adjustColorVal">
                    Adjust Color
                  </label>
                  <Slider onChange={this.handleChange} min={0} max={1} step={0.1} name="adjustColorVal" value={this.state.adjustColorVal}></Slider>
                </div>

                <div className="w-full form-group mt-4">
                  <label className="form-label" htmlFor="randomizeImages">
                    Randomize Images
                  </label>
                  <input
                    type="checkbox"
                    name="randomizeImages"
                    id="randomizeImages"
                    className="scale-150 ml-1 block"
                    onChange={(event) => this.handleChange(event.target.name, event.target.checked)}
                    defaultChecked={this.state.randomizeImages}
                    disabled={this.state.sampleImages.length < 2}
                  ></input>
                </div>
              </div>

              <div className="mt-10 space-y-4">
                <h2 className="text-2xl">Main Image</h2>
                <div>
                  {this.state.targetImage ? (
                    <div>
                      <ImagePreview
                        image={this.state.targetImage}
                        onClose={() => {
                          this.removeMainImage();
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <label htmlFor="main-image-btn" className="add-button">
                        <span>+</span>
                      </label>
                      <input type="file" id="main-image-btn" accept="image/*" hidden onChange={(e) => this.setTargetImage(e.target.files)} />
                    </>
                  )}
                </div>

                <h2 className="text-2xl">Sample Images</h2>

                <div className="w-22 flex flex-wrap">
                  {this.state.sampleImages.map((i: FileExtended, idx: number) => {
                    return (
                      <div key={idx} className="mr-2 mt-2">
                        <ImagePreview
                          image={i}
                          onClose={() => {
                            this.removeSampleImage(idx);
                          }}
                        />
                      </div>
                    );
                  })}

                  <div>
                    <label htmlFor="sample-images-btn" className="add-button">
                      <span>+</span>
                    </label>
                  </div>
                  <input type="file" id="sample-images-btn" accept="image/*" multiple={true} hidden onChange={(e) => this.setSampleImages(e.target.files)} />
                </div>
              </div>
            </div>

            <button
              className="btn btn-brand square w-1/2 render-button-height"
              aria-label="Download Image"
              disabled={this.state.rendering || !this.state.renderedImage}
              onClick={this.downloadImage}
            >
              Download
            </button>

            <button
              className="btn btn-green square w-1/2 render-button-height"
              aria-label="Render Image"
              disabled={!this.state.targetImage || this.state.sampleImages.length === 0 || this.state.rendering}
              onClick={this.renderMosaic}
            >
              Render
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default App;
